import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Wand2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { aiRecipeImage } from '@/services/ai-recipe-image'
import { aiRecipeParser } from '@/services/ai-recipe-parser'
import { useHouseholdStore } from '@/stores/household-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { useSettingsStore } from '@/stores/settings-store'
import type { Recipe } from '@/types/recipe'
import { ParsedRecipePreview } from './parsed-recipe-preview'
import { RecipeTextInput } from './recipe-text-input'

interface RecipeTextParserProps {
  onRecipeParsed?: (recipe: Partial<Recipe>) => void
  className?: string
}

const exampleText = `Klassieke Spaghetti Carbonara

Ingrediënten:
- 400g spaghetti
- 200g pancetta of spek, in blokjes
- 4 eieren
- 100g Parmezaanse kaas, geraspt
- 2 tenen knoflook, gehakt
- Zwarte peper, versgemalen
- Zout

Bereiding:
1. Kook de spaghetti volgens de verpakking in gezouten water.
2. Bak de pancetta in een grote pan tot krokant.
3. Klop eieren met Parmezaanse kaas en zwarte peper.
4. Voeg de gekookte pasta toe aan de pancetta.
5. Haal van het vuur en meng door het ei-kaasmengsel.
6. Serveer direct met extra kaas.

Bereidingstijd: 20 minuten
Porties: 4`

export function RecipeTextParser({ onRecipeParsed, className }: RecipeTextParserProps) {
  const [inputText, setInputText] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [activeTab, setActiveTab] = useState<'text' | 'url'>('text')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parsedRecipe, setParsedRecipe] = useState<Partial<Recipe> | null>(null)
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(aiRecipeParser.peekStatus())
  const [generateImageOnSave, setGenerateImageOnSave] = useState(false)
  const [isRefiningRecipe, setIsRefiningRecipe] = useState(false)
  const [isSavingRecipe, setIsSavingRecipe] = useState(false)
  const [platformAIImageConfigured, setPlatformAIImageConfigured] = useState<boolean | null>(aiRecipeImage.peekStatus())

  const { createRecipe } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const { aiEnabled } = useSettingsStore()
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    void aiRecipeParser.getStatus().then(setPlatformAIConfigured)
    void aiRecipeImage.getStatus().then(setPlatformAIImageConfigured)
  }, [])

  const handleParse = async () => {
    if (activeTab === 'text' && !inputText.trim()) {
      setError('Plak eerst een recepttekst om verder te gaan.')
      return
    }

    if (activeTab === 'url' && !urlInput.trim()) {
      setError('Vul eerst een receptlink in om verder te gaan.')
      return
    }

    const isConfigured = platformAIConfigured ?? (await aiRecipeParser.getStatus())
    setPlatformAIConfigured(isConfigured)

    if (!isConfigured) {
      setError('Deze hulp is nog niet beschikbaar. De beheerder kan dit inschakelen in de instellingen.')
      return
    }

    setIsLoading(true)
    setError(null)
    setParsedRecipe(null)

    try {
      let result

      if (activeTab === 'text') {
        result = await aiRecipeParser.parseRecipeText(inputText)
      } else {
        try {
          new URL(urlInput)
        } catch {
          setError('Deze link lijkt niet geldig. Gebruik een volledige link die begint met http:// of https://.')
          setIsLoading(false)
          return
        }

        result = await aiRecipeParser.parseRecipeFromUrl(urlInput)
      }

      if (result.success && result.recipe) {
        setParsedRecipe(result.recipe)
        setGenerateImageOnSave(false)
        onRecipeParsed?.(result.recipe)
      } else {
        setError(result.error || 'Het lukte niet om van deze invoer een bruikbaar recept te maken.')
      }
    } catch {
      setError('Er ging iets mis tijdens het verwerken. Probeer het nog eens.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveRecipe = async () => {
    if (!parsedRecipe || !parsedRecipe.title || !parsedRecipe.ingredients || !parsedRecipe.instructions) {
      return
    }

    setIsSavingRecipe(true)

    let recipeToSave = parsedRecipe
    if (generateImageOnSave && !recipeToSave.image_url && platformAIImageConfigured !== false) {
      const imageResult = await aiRecipeImage.generateRecipeImage(recipeToSave)
      if (imageResult.success && imageResult.imageUrl) {
        recipeToSave = { ...recipeToSave, image_url: imageResult.imageUrl }
        setParsedRecipe(recipeToSave)
      } else {
        toast({
          title: 'Recept opgeslagen zonder foto',
          description: imageResult.error || 'De app kon geen passende afbeelding maken.',
          variant: 'destructive',
        })
      }
    }

    const createdRecipe = await createRecipe(
      {
        title: recipeToSave.title || '',
        description: recipeToSave.description || '',
        ingredients: recipeToSave.ingredients || [],
        instructions: recipeToSave.instructions || [],
        prep_time: recipeToSave.prep_time || null,
        cook_time: recipeToSave.cook_time || null,
        servings: recipeToSave.servings || null,
        difficulty: recipeToSave.difficulty || '',
        meal_type: recipeToSave.meal_type || '',
        tags: recipeToSave.tags || [],
        equipment: recipeToSave.equipment || [],
        image_url: recipeToSave.image_url || '',
        rating: recipeToSave.rating || null,
        notes: recipeToSave.notes || '',
      },
      viewMode === 'household' ? currentHousehold?.id : undefined
    )

    setIsSavingRecipe(false)

    if (createdRecipe) {
      navigate(`/recepten/${createdRecipe.id}`)
    }
  }

  const handleRefineRecipe = async (instruction: string) => {
    if (!parsedRecipe || !instruction.trim()) {
      return
    }

    setIsRefiningRecipe(true)
    const result = await aiRecipeParser.refineRecipe(parsedRecipe, instruction)
    setIsRefiningRecipe(false)

    if (!result.success || !result.recipe) {
      toast({
        title: 'Aanpassen lukte niet',
        description: result.error || 'De app kon deze wijziging niet goed verwerken.',
        variant: 'destructive',
      })
      return
    }

    const refinedRecipe = result.recipe

    setParsedRecipe((current) => ({
      ...(current || {}),
      ...refinedRecipe,
      image_url: current?.image_url || refinedRecipe.image_url,
    }))
    toast({
      title: 'Recept bijgewerkt',
      description: 'Je wijzigingen zijn toegepast op het voorbeeld.',
    })
  }

  if (!aiEnabled || platformAIConfigured === false) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Wand2 className="mr-2 h-5 w-5" />
            Recept overnemen met hulp
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Deze hulp is nu niet beschikbaar</h3>
            <p className="mb-4 text-gray-600">
              De beheerder moet dit eerst inschakelen. Tot die tijd kun je recepten nog wel handmatig toevoegen.
            </p>
            <Button variant="outline" onClick={() => navigate('/instellingen')}>
              Naar instellingen
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Wand2 className="mr-2 h-5 w-5" />
            Recept overnemen met hulp
            <Badge variant="secondary" className="ml-2">
              Slimme hulp
            </Badge>
          </CardTitle>
          <p className="text-sm text-gray-600">
            Geef een recepttekst of link. De app maakt daar een invulbaar recept van dat je daarna rustig kunt controleren en aanpassen.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <RecipeTextInput
            activeTab={activeTab}
            inputText={inputText}
            urlInput={urlInput}
            isLoading={isLoading}
            onTabChange={setActiveTab}
            onInputTextChange={setInputText}
            onUrlInputChange={setUrlInput}
            onParse={handleParse}
            onLoadExample={() => setInputText(exampleText)}
            onClear={() => {
              setInputText('')
              setUrlInput('')
              setParsedRecipe(null)
              setError(null)
            }}
          />

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start">
                <AlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                <div className="flex-1 whitespace-pre-line text-sm text-red-800">{error}</div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {parsedRecipe ? (
        <ParsedRecipePreview
          parsedRecipe={parsedRecipe}
          onSave={handleSaveRecipe}
          onRefineRecipe={handleRefineRecipe}
          isRefiningRecipe={isRefiningRecipe}
          generateImageOnSave={generateImageOnSave}
          onGenerateImageOnSaveChange={setGenerateImageOnSave}
          aiImageAvailable={platformAIImageConfigured !== false}
          isSaving={isSavingRecipe}
          onImageChange={(imageUrl) =>
            setParsedRecipe((current) => (current ? { ...current, image_url: imageUrl } : current))
          }
        />
      ) : null}
    </div>
  )
}
